import React from 'react';
import { useParams } from 'react-router-dom';
import dashboardWrapper from '~/wrappers/dashboard';

import BackLink from '~/components/BackLink';
import { t } from '~/i18n';
import { Panel, Spinner } from '@ixicare/ui';
import AlertMapContainer from '~/containers/Alerts/AlertMap';
import AlertStopWatchContainer from '~/containers/Alerts/AlertStopWatch';
import AlertActionsContainer from '~/containers/Alerts/AlertActions';
import AlertLogs from '~/components/AlertLogs';
import AlertCancelledTriggerContainer from '~/containers/Alerts/Triggers/AlertCancelled';
import AlertIsAcceptedTriggerContainer from '~/containers/Alerts/Triggers/AlertIsAccepted';
import AlertHeader from '~/components/AlertHeader';
import SoundService from '~services/SoundService';
import { useStores } from '~store/utils';
import { observer } from 'mobx-react';
import AlertSegmentationLogs from '~components/AlertSegmentationLogs';

const AlertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { alerts } = useStores();

  const alert = alerts.alertStoreMap.get(id!);
  const parentAlert = alert?.parentAlertId ? alerts.alertStoreMap.get(alert.parentAlertId) : undefined;

  React.useEffect(() => {
    if (id) {
      alerts.fetchAlert(id);
    }
  }, [id, alerts]);

  React.useEffect(() => {
    if (alert?.parentAlertId && !alerts.alertStoreMap.has(alert.parentAlertId)) {
      alerts.fetchAlert(alert.parentAlertId);
    }
  }, [alert?.parentAlertId, alerts]);

  React.useEffect(() => {
    const muteId = SoundService.mute();
    return () => {
      SoundService.unmute(muteId);
    };
  }, []);

  if (!alert) {
    return <Spinner label={t('global.loading')} />;
  }
  return (
    <>
      <BackLink fallbackUrl="/alerts" name={t('alarmDetail.goBack')} />
      <div className="mb-10">
        <AlertHeader alert={alert} parentAlert={parentAlert} />
      </div>
      <div className="row mb-30">
        <div className="col-md-4">
          <Panel className="d-flex align-items-center">
            <AlertStopWatchContainer alert={alert} />
          </Panel>
        </div>
        <div className="col-md-8">
          <Panel>
            {alert.device || parentAlert?.device?.location?.lastKnown ? (
              <AlertMapContainer alert={alert} parentAlert={parentAlert} />
            ) : (
              <h2>{alert.locationDescription}</h2>
            )}
          </Panel>
        </div>
      </div>
      <div className="row" style={{ marginBottom: 80 }}>
        <div className="col-md-6">
          <Panel>
            <Panel.Title>{t('alert.logs.title')}</Panel.Title>
            <AlertLogs alert={alert} />
          </Panel>
        </div>
        <div className="col-md-6">
          <Panel>
            <Panel.Title>{t('alert.segmentationLogs.title')}</Panel.Title>
            <AlertSegmentationLogs alert={alert} />
          </Panel>
        </div>
      </div>
      <AlertActionsContainer alert={alert} />
      <AlertCancelledTriggerContainer alert={alert} />
      <AlertIsAcceptedTriggerContainer alert={alert} />
    </>
  );
};
const ObservedAlertDetail = observer(AlertDetail);

export default dashboardWrapper(ObservedAlertDetail);
